// Horizontal grid lines for the multi-axis pump charts.
//
// Only one Y axis (the first visible one) draws grid lines, so 2-4 channel
// axes never stack several sets of lines. Highcharts' chart.alignTicks (on by
// default) gives every other axis with data the same tick count, so their
// labels land on those same lines. Grid lines therefore always match the
// labels instead of coming from a fixed overlay that ignores the data.

import type Highcharts from "highcharts";
import { isWholeNumberChannel } from "./pumpChannels.ts";

export const chartGridLineColor = (isDarkTheme: boolean): string =>
  isDarkTheme ? "rgba(255, 255, 255, 0.28)" : "rgba(0, 0, 0, 0.18)";

// Grid and tick options for one channel Y axis.
export const channelAxisGridOptions = ({
  drawsGrid,
  channelValues,
  isDarkTheme,
}: {
  drawsGrid: boolean;
  channelValues: string[];
  isDarkTheme: boolean;
}): Highcharts.YAxisOptions => ({
  gridLineWidth: drawsGrid ? 1 : 0,
  gridLineColor: chartGridLineColor(isDarkTheme),
  gridLineDashStyle: "Solid",
  minorGridLineWidth: 0,
  // Whole-number channels (Gear 0, 1, 2 — never 2.5) get whole-number labels.
  // Mixed axes (e.g. Gear + RPM) keep normal ticks so the other channel
  // isn't squeezed onto whole-number steps.
  ...(channelValues.length > 0 && channelValues.every(isWholeNumberChannel)
    ? { allowDecimals: false }
    : {}),
});

// Placeholder grid for an empty chart (no channel axis visible). It keeps its
// slot in the yAxis array so later axis indexes don't shift, and draws no
// lines once a channel axis is showing.
export const emptyChartGridAxis = (
  isDarkTheme: boolean,
  enabled: boolean,
): Highcharts.YAxisOptions => ({
  min: 0,
  max: 5,
  tickPositions: [0, 1, 2, 3, 4, 5],
  visible: enabled,
  showEmpty: true,
  labels: { enabled: false },
  title: { text: undefined },
  lineWidth: 0,
  tickWidth: 0,
  gridLineWidth: enabled ? 1 : 0,
  gridLineColor: chartGridLineColor(isDarkTheme),
  gridLineDashStyle: "Solid",
  minorGridLineWidth: 0,
  // Never take part in tick alignment with the channel axes.
  alignTicks: false,
});
